import { areArrayEquals, objectEntries, PlainObjectTraverser, type ReadonlyRecord } from '../../utils'

import { ConnectwareError, ConnectwareErrorType } from '../../domain'

import { EnvConfigurationError } from '../../application'

import type { ConfigurationMetadata, ConfigurationMetadataSupportedTypes } from '../config'
import { createValidator } from '../schema'

type DeserializedResult = Readonly<{ value: unknown, valid: true }> | Readonly<{ valid: false }>
const deserializers: ReadonlyRecord<ConfigurationMetadataSupportedTypes, (raw: string) => DeserializedResult> = {
    integer: (value) => {
        const number = Number(value)
        return Number.isInteger(number) ? { value: number, valid: true } : { valid: false }
    },
    null: (value) => (value === '' ? { value: null, valid: true } : { valid: false }),
    number: (value) => {
        const number = Number(value)
        return !isNaN(number) ? { value: number, valid: true } : { valid: false }
    },
    string: (value) => ({ value, valid: true }),
    array: (value) => {
        try {
            return { value: JSON.parse(value), valid: true }
        } catch (e) {
            return { valid: false }
        }
    },
}

/**
 * Utility class to customize application configuration
 */
export class ConfigurationCustomizer<Config> {
    private readonly unexpectedErrors: ConnectwareError[] = []

    private static isValid (config: unknown): config is ReadonlyRecord<string, string> {
        return typeof config === 'object' && config !== null && objectEntries(config).every((entries) => entries.every((v) => typeof v === 'string'))
    }

    private static resolveCustomization ({ path, schema }: ConfigurationMetadata, env: string, override: string): ConnectwareError | unknown {
        const isValid = createValidator(schema, 'isValid')

        for (const possibleType of schema.type) {
            /** With the given type, attempt to convert it from a string to the target type */
            const deserialize = possibleType in deserializers && deserializers[possibleType as keyof typeof deserializers]

            if (!deserialize) {
                /**
                 * Deserialization errors are not expected,
                 * this means there is a missing deserializer in the codebase. Stop trying
                 */
                return new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Can not de-serialize to the expected type', { type: possibleType })
            }

            const result = deserialize(override)

            if (!result.valid || !isValid(result.value)) {
                /** This may be invalid, but other entries in the loop may be fine */
                continue
            }

            return result.value
        }

        return new EnvConfigurationError(path, env, override)
    }

    constructor (
        /** Base configuration */
        private config: Config,

        /** What can be customized */
        private readonly customizationInstructions: ReadonlyRecord<string, ConfigurationMetadata>
    ) {}

    get errors (): readonly ConnectwareError[] {
        return this.unexpectedErrors
    }

    get customizedConfig (): Config {
        return this.config
    }

    private replaceConfiguration (customization: ConfigurationMetadata, env: string, override: string): void {
        this.config = new PlainObjectTraverser<typeof this.config>((oldConfig, parents) => {
            if (!areArrayEquals(parents, customization.path)) {
                /** Not relevant to the current configurable entry */
                return oldConfig
            }

            const result = ConfigurationCustomizer.resolveCustomization(customization, env, override)

            if (ConnectwareError.is(result)) {
                this.addError(result)
                return oldConfig
            }

            return result as typeof oldConfig
        }).traverse(this.config)
    }

    addError (error: ConnectwareError): this {
        this.unexpectedErrors.push(error)
        return this
    }

    addCustomConfig (customization: unknown): this {
        if (!ConfigurationCustomizer.isValid(customization)) {
            return this.addError(new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Custom config is invalid', { config: customization }))
        }

        for (const [env, value] of objectEntries(customization)) {
            const configurable = this.customizationInstructions[env]

            if (!configurable) {
                this.addError(new ConnectwareError(ConnectwareErrorType.UNEXPECTED, 'Unexpected customization entry', { env }))
                continue
            }

            this.replaceConfiguration(configurable, env, value)
        }

        return this
    }
}
